import React, { useCallback, useEffect, useState } from 'react'
import { Link } from '@faststore/ui'
import HeartOutlineIcon from 'src/images/svg/icon-heart-outline'
import HeartFilledIcon from 'src/images/svg/icon-heart-filled'
import { useUserContext } from 'src/contexts/user-context'
import { useWishlistContext } from 'src/contexts/WishlistContext/wishlist-context'
import isProductOnWishlist from 'src/utils/wishlist/isProductOnWishlist'
import type {
  BrowserProductQueryQuery,
  ProductSummary_ProductFragment,
} from '@generated/graphql'

import storeConfig from '../../../../store.config'
import IconButton from '../VivaraIconButton'

import './WishlistHeart.module.scss'

interface WishlistButtonProps {
  readonly products: ReadonlyArray<
    BrowserProductQueryQuery['product'] | ProductSummary_ProductFragment
  >
  readonly hasBackgroundColor?: boolean
  readonly isOnShelf?: boolean
  readonly onFilledHeartClick: () => void
  readonly onEmptyHeartClick: () => void
  readonly pdpPath: string
  readonly wishlistStyle?: React.CSSProperties
}

export default function WishlistHeart({
  products,
  hasBackgroundColor = true,
  isOnShelf = false,
  onFilledHeartClick,
  onEmptyHeartClick,
  pdpPath,
  wishlistStyle,
}: WishlistButtonProps) {
  const [isOnWishlist, setIsOnWishlist] = useState(false)
  const { user } = useUserContext()
  const { lists } = useWishlistContext()
  const returnUrl = encodeURIComponent(pdpPath)

  const isWishlist = useCallback(() => {
    setIsOnWishlist(
      products?.some((element) =>
        isProductOnWishlist(element?.sku ?? element?.id, lists)
      )
    )
  }, [lists, products])

  const HeartIcon = isOnWishlist ? (
    <HeartFilledIcon wishlistStyle={wishlistStyle} />
  ) : (
    <HeartOutlineIcon wishlistStyle={wishlistStyle} />
  )

  useEffect(() => {
    isWishlist()
  }, [isWishlist])

  useEffect(() => {
    if (
      !isOnWishlist ||
      !window.insider_object ||
      window.insider_object.wishlist ||
      window.insider_object?.page?.originalType !== 'Product'
    ) {
      return
    }

    window.insider_object.wishlist = {
      currency: window.insider_object.product.currency,
      total: window.insider_object.product.unit_price,
      line_items: [
        {
          product: window.insider_object.product,
          quantity: 1,
          subtotal: window.insider_object.product.unit_price as number,
        },
      ],
    }
  }, [isOnWishlist])

  return (
    <div
      data-wishlist-button
      data-wishlist-button-bg={hasBackgroundColor}
      data-wishlist-shelf={isOnShelf}
    >
      {!user ? (
        <Link
          data-login-link
          href={`${storeConfig.secureSubdomain}/login?returnUrl=${returnUrl}`}
        >
          <IconButton icon={HeartIcon} aria-label="Favoritar" />
        </Link>
      ) : (
        <IconButton
          icon={HeartIcon}
          aria-label={isOnWishlist ? 'Desfavoritar' : 'Favoritar'}
          onClick={(e) => {
            e.preventDefault()
            if (isOnWishlist) {
              onFilledHeartClick()

              return
            }

            onEmptyHeartClick()
          }}
        />
      )}
    </div>
  )
}
